import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, DatePicker, Flex, Input, InputNumber, Modal, Typography } from 'antd'
import { editIssuedCheck } from 'src/app/shared/services/IssuedCheckService'
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import RichTextField from 'src/app/shared/components/common/rich-text-field'
import { NotificationInstance } from 'antd/es/notification/interface'
import dayjs from 'dayjs'
import { FORMAT_DATE } from 'src/app/shared/constants/app.constants'

export interface EditIssuedCheckModalProps {
    isOpen: boolean
    onClose: () => void
    rowData: any
    notiApi: NotificationInstance
    onGridReady?: () => any
}

const EditIssuedCheckModal = (props: EditIssuedCheckModalProps) => {
    const { isOpen, onClose, rowData, notiApi, onGridReady } = props
    const [loading, setLoading] = useState(false)
    const { t } = useTranslation()

    const [issuedDate, setIssuedDate] = useState<dayjs.Dayjs | null>(null)
    const [issuedPayee, setIssuedPayee] = useState('')
    const [amount, setAmount] = useState<number | null>(null)

    useEffect(() => {
        if (isOpen && rowData) {
            setIssuedDate(rowData.issuedDate ? dayjs(rowData.issuedDate) : null)
            setIssuedPayee(rowData.issuedPayee ?? '')
            const rawAmount = typeof rowData.amount === 'string'
                ? parseFloat(rowData.amount.replace(/[$,]/g, ''))
                : rowData.amount
            setAmount(rawAmount != null && !isNaN(rawAmount) ? rawAmount : null)
        }
    }, [isOpen, rowData])

    const isValid = issuedDate && issuedDate.isValid() && amount !== null && amount > 0

    const handleConfirm = async () => {
        try {
            if (rowData?.id && isValid) {
                setLoading(true)
                const response = await editIssuedCheck({
                    id: rowData.id,
                    issuedDate: issuedDate!.format('YYYY-MM-DD'),
                    issuedPayee,
                    amount: amount!,
                })

                if (response?.statusCode === 'Success') {
                    notiApi.success({
                        message: (
                            <RichTextField
                                richText={t('SystemMessages:' + SYSTEM_MESSAGE_IDS.ISSUED_CHECK_UPDATED_SUCCESSFULLY, {
                                    checkNumber: rowData.checkNumber,
                                })}
                            />
                        ),
                        closeIcon: null,
                    })
                }
            }
        } catch (e) {
        } finally {
            onGridReady?.()
            onClose()
            setLoading(false)
        }
    }

    return (
        <Modal
            title={t('TransactionResult:modal.title.editIssuedCheck')}
            centered
            open={isOpen}
            onCancel={onClose}
            keyboard={false}
            maskClosable={false}
            footer={[
                <Button type='default' onClick={onClose} disabled={loading} key='cancel'>
                    {t('common:cancel')}
                </Button>,
                <Button type='primary' key='confirm' onClick={handleConfirm} disabled={loading || !isValid} loading={loading}>
                    {t('common:save')}
                </Button>,
            ]}
            width={500}
        >
            <Flex vertical gap={16} style={{ paddingTop: 8 }}>
                <div>
                    <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>
                        {t('TransactionResult:table.checkNumber')}
                    </Typography.Text>
                    <Input value={rowData?.checkNumber} disabled />
                </div>

                <div>
                    <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>
                        {t('TransactionResult:table.issuedDate')}
                    </Typography.Text>
                    <DatePicker
                        value={issuedDate}
                        onChange={(date) => setIssuedDate(date)}
                        format={FORMAT_DATE}
                        style={{ width: '100%' }}
                    />
                </div>

                <div>
                    <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>
                        {t('TransactionResult:table.issuedPayee')}
                    </Typography.Text>
                    <Input
                        value={issuedPayee}
                        onChange={(e) => setIssuedPayee(e.target.value)}
                        maxLength={255}
                    />
                </div>

                <div>
                    <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>
                        {t('TransactionResult:table.amount')}
                    </Typography.Text>
                    <InputNumber
                        value={amount}
                        onChange={(val) => setAmount(val)}
                        min={0}
                        precision={2}
                        style={{ width: '100%' }}
                    />
                </div>
            </Flex>
        </Modal>
    )
}

export default EditIssuedCheckModal
